import React, { useContext, useEffect, useState } from 'react';
import { NavigationContext } from '~/context/navigation';
import { useLayout } from '../../Layout/LayoutContext';
import { InformacaoTelasAcessadasContext } from '~/context/InformacaoTelasAcessadas';
import { MenuItem, MenuLink } from '../styles';
import { FaEllipsisV, FaStar, FaRegStar } from 'react-icons/fa';
import { useMenus } from './MenusContext';
import { StorageService } from '~/utils/classes/StorageService';

interface TelaData {
  cod_tela: string;
  url?: string;
  des_tela: string;
  Icon?: any;
  content?: any;
  qtd_acesso?: number;
  dta_ult_acesso?: Date;
}

interface RenderTelasListProps {
  telas: TelaData[];
}

export const processMoreAccessedTelas = (
  moreAccessed: any,
  telas: any[],
): any[] => {
  if (!moreAccessed || !moreAccessed.data.length || !telas.length) return [];

  const allTelas: any[] = [];
  telas.forEach((modulo) => {
    if (modulo.telas) {
      modulo.telas.forEach((tela: any) => {
        allTelas.push(tela);
      });
    }
  });

  const telasMaisAcessadas = allTelas.filter((tela) => {
    const acessos = moreAccessed.data.find(
      (ma: any) => ma.cod_tela === tela.cod_tela,
    );
    if (acessos) {
      tela.qtd_acesso = acessos.qtd_acesso;
    }
    return moreAccessed.codes.includes(tela.cod_tela);
  });

  telasMaisAcessadas.sort((a, b) => {
    if (a.qtd_acesso && b.qtd_acesso) {
      return Number(b.qtd_acesso) - Number(a.qtd_acesso);
    }
    return 0;
  });

  return telasMaisAcessadas;
};

export const processRecentTelas = (recentes: any, telas: any[]): any[] => {
  if (!recentes || !recentes.data.length || !telas.length) return [];

  const allTelas: any[] = [];
  telas.forEach((modulo) => {
    if (modulo.telas) {
      modulo.telas.forEach((tela: any) => {
        allTelas.push(tela);
      });
    }
  });

  const telasRecentes = allTelas.filter((tela) => {
    const acessos = recentes.data.find(
      (telaRecente: any) => telaRecente.cod_tela === tela.cod_tela,
    );
    if (acessos) {
      tela.dta_ult_acesso = new Date(acessos.dta_ult_acesso);
    }
    return recentes.codes.includes(tela.cod_tela);
  });

  telasRecentes.sort((a: any, b: any) => {
    if (a.dta_ult_acesso && b.dta_ult_acesso) {
      return b.dta_ult_acesso.getTime() - a.dta_ult_acesso.getTime();
    }
    return 0;
  });

  return telasRecentes;
};

export const processFavoriteTelas = (telasPorModulo: any[]): any[] => {
  if (!telasPorModulo.length) return [];
  const allTelas: any[] = [];

  telasPorModulo.forEach((modulo) => {
    if (modulo.telas) {
      modulo.telas.forEach((tela: any) => {
        allTelas.push(tela);
      });
    }
  });

  return allTelas.filter((tela) => tela.flg_favorita === true);
};

export const RenderTelasList: React.FC<RenderTelasListProps> = ({ telas }) => {
  const { addScreen } = useContext(NavigationContext);
  const { addScreenInfo } = useContext(InformacaoTelasAcessadasContext);
  const { setCollapsed, setSelectedModulo } = useLayout();
  const { handleFavorite, handleAddAccessed, setViewMode } = useMenus();
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const store = new StorageService();

  const handleTelaClick = (tela: any) => {
    if (tela.content) {
      // Chamar handleAddAccessed sempre que uma tela for clicada
      handleAddAccessed(tela.cod_tela);

      addScreen(tela);
      addScreenInfo(tela);
      setCollapsed(true);
      store.save('session', 'collapsed', true);

      // Resetar para o estado padrão (modules) com módulo base (2)
      setViewMode('modules');
      setSelectedModulo(2);
    }
  };

  const toggleDropdown = (e: React.MouseEvent, telaId: string) => {
    e.preventDefault();
    e.stopPropagation();
    setOpenDropdown(openDropdown === telaId ? null : telaId);
  };

  const handleFavoriteClick = async (e: React.MouseEvent, tela: any) => {
    e.preventDefault();
    e.stopPropagation();

    const favoriteStatus = tela.flg_favorita;
    await handleFavorite(tela.cod_tela, favoriteStatus);
    setOpenDropdown(null);
  };

  // Fechar dropdown ao clicar fora
  useEffect(() => {
    const handleClickOutside = () => setOpenDropdown(null);
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  if (!telas || telas.length === 0) {
    return (
      <MenuItem>
        <div style={{ padding: '20px', color: '#fff', textAlign: 'center' }}>
          Nenhuma tela encontrada
        </div>
      </MenuItem>
    );
  }

  return (
    <>
      {telas.map((tela: any) => (
        <MenuItem key={tela.cod_tela}>
          <MenuLink
            to={tela.url || '/app'}
            onClick={(e) => {
              // Se clicou no botão de ações, não abrir a tela
              if ((e.target as HTMLElement).closest('.actions-button')) {
                e.preventDefault();
                return;
              }

              handleTelaClick(tela);
            }}
          >
            {tela.Icon && <tela.Icon size={18} />}
            {tela.des_tela}

            {tela.flg_favorita && (
              <span className="favorite-indicator" title="Tela favoritada" />
            )}

            <button
              type="button"
              className="actions-button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                toggleDropdown(e, tela.cod_tela.toString());
              }}
              title="Ações"
            >
              <FaEllipsisV size={12} />
            </button>

            {openDropdown === tela.cod_tela.toString() && (
              <div className="dropdown">
                <button
                  type="button"
                  className="dropdown-item"
                  onClick={(e) => handleFavoriteClick(e, tela)}
                >
                  {tela.flg_favorita ? (
                    <>
                      <FaStar size={14} />
                      Remover dos favoritos
                    </>
                  ) : (
                    <>
                      <FaRegStar size={14} />
                      Adicionar aos favoritos
                    </>
                  )}
                </button>
              </div>
            )}
          </MenuLink>
        </MenuItem>
      ))}
    </>
  );
};
